import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  RefreshCw,
  Search,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import { adminRequest } from './api';
import { PROVIDER_OPTIONS, type ModelCapability, type PresetModel, type ProviderOption } from './providerPresets';
import { showToast } from './Toast';
import { ProviderBadge } from './ProviderIcon';

type ModelModalProps = {
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (error: unknown) => void;
};

type ProviderCatalogResponse = {
  providers: Array<ProviderOption & { configured: boolean; maskedHint: string | null }>;
};

type TestConnectionResponse = {
  ok: boolean;
  latencyMs: number;
  message: string;
  model: string;
  error?: string;
};

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) {
    const m = tokens / 1_000_000;
    return `${Number.isInteger(m) ? m : m.toFixed(1)}M ctx`;
  }
  return `${Math.round(tokens / 1000)}k ctx`;
}

function formatPrice(val: number): string {
  if (val === 0) return 'Free';
  if (val < 0.1) return `$${val.toFixed(3)}`;
  return `$${val.toFixed(2)}`;
}

export function ModelModal({ onClose, onSuccess, onError }: ModelModalProps) {
  const [selectedProvider, setSelectedProvider] = useState<string>('openrouter');
  const [providersData, setProvidersData] = useState<ProviderCatalogResponse['providers']>([]);
  const [useExistingKey, setUseExistingKey] = useState<boolean>(false);
  const [apiKey, setApiKey] = useState<string>('');
  const [showApiKey, setShowApiKey] = useState<boolean>(false);

  const [modelKey, setModelKey] = useState<string>('anthropic/claude-3.7-sonnet');
  const [displayName, setDisplayName] = useState<string>('Claude 3.7 Sonnet');
  const [baseUrl, setBaseUrl] = useState<string>('https://openrouter.ai/api/v1');
  const [capabilities, setCapabilities] = useState<Set<ModelCapability>>(new Set(['text', 'vision', 'structured_output']));
  const [inputPrice, setInputPrice] = useState<string>('3.00');
  const [outputPrice, setOutputPrice] = useState<string>('15.00');
  const [cachePrice, setCachePrice] = useState<string>('0.30');
  const [contextTokens, setContextTokens] = useState<string>('200000');
  const [maxOutputTokens, setMaxOutputTokens] = useState<string>('64000');

  const [fetchedLiveModels, setFetchedLiveModels] = useState<PresetModel[]>([]);
  const [isLiveCatalog, setIsLiveCatalog] = useState<boolean>(false);
  const [isFetchingLive, setIsFetchingLive] = useState<boolean>(false);
  const [liveFetchError, setLiveFetchError] = useState<string | null>(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [modelSearchQuery, setModelSearchQuery] = useState<string>('');
  const [isCustomMode, setIsCustomMode] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [isProviderDropdownOpen, setIsProviderDropdownOpen] = useState<boolean>(false);
  const providerDropdownRef = useRef<HTMLDivElement>(null);

  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'failed'>('idle');
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [activeModelIndex, setActiveModelIndex] = useState<number>(0);
  const modelListRef = useRef<HTMLDivElement>(null);

  const currentProviderConfig = useMemo(() => {
    return PROVIDER_OPTIONS.find((p) => p.key === selectedProvider) ?? PROVIDER_OPTIONS[0]!;
  }, [selectedProvider]);

  const configuredProviderInfo = useMemo(() => {
    return providersData.find((p) => p.key === selectedProvider);
  }, [providersData, selectedProvider]);

  const availableModels = useMemo(() => {
    if (fetchedLiveModels.length > 0) return fetchedLiveModels;
    return currentProviderConfig?.models || [];
  }, [fetchedLiveModels, currentProviderConfig]);

  const applyModelPreset = (model: PresetModel) => {
    setModelKey(model.modelKey);
    setDisplayName(model.displayName);
    setContextTokens(String(model.maxInputTokens));
    setMaxOutputTokens(String(model.maxOutputTokens));
    setCapabilities(new Set(model.capabilities));
    setInputPrice(model.pricingRates.inputPerMillionTokens !== null ? String(model.pricingRates.inputPerMillionTokens) : '0');
    setOutputPrice(model.pricingRates.outputPerMillionTokens !== null ? String(model.pricingRates.outputPerMillionTokens) : '0');
    setCachePrice(model.pricingRates.cacheReadPerMillionTokens !== null && model.pricingRates.cacheReadPerMillionTokens !== undefined ? String(model.pricingRates.cacheReadPerMillionTokens) : '');
    setTestStatus('idle');
    setTestResult(null);
  };

  const fetchLiveModelsForProvider = async (
    provKey: string,
    keyOverride?: string,
    usingExistingOverride?: boolean,
    baseUrlOverride?: string,
    preserveCurrentSelection?: boolean
  ) => {
    const isConfiguredKey = usingExistingOverride !== undefined ? usingExistingOverride : useExistingKey;
    const effectiveKey = keyOverride !== undefined ? keyOverride : (isConfiguredKey ? undefined : apiKey.trim() || undefined);
    const effectiveBaseUrl = baseUrlOverride || baseUrl;

    if (provKey === 'gemini' && !effectiveKey && !isConfiguredKey) {
      setFetchedLiveModels(PROVIDER_OPTIONS.find((p) => p.key === 'gemini')?.models || []);
      setIsLiveCatalog(false);
      return;
    }

    setIsFetchingLive(true);
    setLiveFetchError(null);
    try {
      const models = await adminRequest<PresetModel[]>('/providers/fetch-models', {
        method: 'POST',
        body: JSON.stringify({
          providerKey: provKey,
          apiKey: effectiveKey,
          baseUrl: effectiveBaseUrl,
        }),
      });

      if (models && models.length > 0) {
        setFetchedLiveModels(models);
        setIsLiveCatalog(Boolean(effectiveKey || isConfiguredKey || provKey === 'openrouter'));
        if (!preserveCurrentSelection) {
          const match = models.find((m) => m.modelKey === modelKey);
          if (match) {
            applyModelPreset(match);
          } else if (models[0]) {
            applyModelPreset(models[0]);
          }
        }
      } else {
        setLiveFetchError('No models returned from provider API.');
        setIsLiveCatalog(false);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch models from provider API';
      setLiveFetchError(message);
      setIsLiveCatalog(false);
    } finally {
      setIsFetchingLive(false);
    }
  };

  useEffect(() => {
    adminRequest<ProviderCatalogResponse>('/providers')
      .then((res) => {
        if (res?.providers) {
          setProvidersData(res.providers);
          const current = res.providers.find((p) => p.key === selectedProvider);
          const isConfigured = Boolean(current?.configured);
          if (isConfigured) setUseExistingKey(true);
          fetchLiveModelsForProvider(selectedProvider, undefined, isConfigured);
        } else {
          fetchLiveModelsForProvider(selectedProvider);
        }
      })
      .catch(() => {
        fetchLiveModelsForProvider(selectedProvider);
      });
  }, []);

  useEffect(() => {
    if (useExistingKey) return;
    const trimmed = apiKey.trim();
    if (!trimmed) return;
    if (
      (selectedProvider === 'gemini' && trimmed.length >= 15) ||
      (selectedProvider === 'openrouter' && trimmed.length >= 20) ||
      (selectedProvider === 'openai' && trimmed.length >= 20) ||
      (selectedProvider === 'deepseek' && trimmed.length >= 20) ||
      (selectedProvider === 'custom' && trimmed.length >= 10)
    ) {
      const timer = setTimeout(() => {
        fetchLiveModelsForProvider(selectedProvider, trimmed, false, baseUrl, true);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [apiKey, selectedProvider, useExistingKey, baseUrl]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }
      if (providerDropdownRef.current && !providerDropdownRef.current.contains(target)) {
        setIsProviderDropdownOpen(false);
      }
    }
    if (isDropdownOpen || isProviderDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen, isProviderDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 40);
      return () => clearTimeout(timer);
    } else {
      setModelSearchQuery('');
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    setActiveModelIndex(0);
  }, [modelSearchQuery, availableModels]);

  const filteredModels = useMemo(() => {
    const q = modelSearchQuery.trim().toLowerCase();
    if (!q) return availableModels;
    const tokens = q.split(/\s+/).filter(Boolean);
    return availableModels.filter((m) => {
      const name = m.displayName.toLowerCase();
      const key = m.modelKey.toLowerCase();
      const isVision = m.capabilities.includes('vision');
      return tokens.every((token) => {
        if (token === 'vision' || token === 'image' || token === 'multimodal') return isVision;
        return name.includes(token) || key.includes(token);
      });
    });
  }, [availableModels, modelSearchQuery]);

  const selectedModelPreset = useMemo(() => {
    return availableModels.find((m) => m.modelKey === modelKey);
  }, [availableModels, modelKey]);

  const handleProviderSelect = (provKey: string) => {
    setSelectedProvider(provKey);
    setIsProviderDropdownOpen(false);
    const prov = PROVIDER_OPTIONS.find((p) => p.key === provKey) ?? PROVIDER_OPTIONS[0]!;
    setBaseUrl(prov.baseUrl);
    setFetchedLiveModels([]);
    setIsLiveCatalog(false);
    setLiveFetchError(null);
    setTestStatus('idle');
    setTestResult(null);
    setIsDropdownOpen(false);
    setModelSearchQuery('');
    setIsCustomMode(false);

    const configured = Boolean(providersData.find((p) => p.key === provKey)?.configured);
    setUseExistingKey(configured);
    if (!configured) setApiKey('');

    if (prov.models.length > 0 && prov.models[0]) {
      applyModelPreset(prov.models[0]);
    } else {
      setModelKey('');
      setDisplayName('');
      setInputPrice('');
      setOutputPrice('');
      setCachePrice('');
      setContextTokens('128000');
      setMaxOutputTokens('4096');
      setCapabilities(new Set(['text']));
    }

    if (provKey === 'openrouter' || configured) {
      fetchLiveModelsForProvider(provKey, configured ? undefined : '', configured, prov.baseUrl);
    }
  };

  const toggleCapability = (cap: ModelCapability) => {
    setCapabilities((prev) => {
      const next = new Set(prev);
      if (next.has(cap)) next.delete(cap);
      else next.add(cap);
      return next;
    });
  };

  const handleFetchLiveModels = async () => {
    const isConfiguredKey = useExistingKey;
    const effectiveKey = isConfiguredKey ? undefined : apiKey.trim() || undefined;
    if (selectedProvider === 'gemini' && !effectiveKey && !isConfiguredKey) {
      setLiveFetchError('Please enter a Google Gemini API key above to load all models from Google AI Studio.');
      return;
    }
    await fetchLiveModelsForProvider(selectedProvider, effectiveKey, isConfiguredKey, baseUrl, true);
  };

  const handleTestConnection = async () => {
    setTestStatus('testing');
    setTestResult(null);
    try {
      const result = await adminRequest<TestConnectionResponse>('/providers/test-connection', {
        method: 'POST',
        body: JSON.stringify({
          providerKey: selectedProvider,
          modelKey,
          baseUrl,
          apiKey: useExistingKey ? undefined : apiKey.trim() || undefined,
        }),
      });

      setTestResult(result);
      setTestStatus(result.ok ? 'success' : 'failed');
      if (result.ok) {
        showToast({
          type: 'success',
          title: 'Connection Verified',
          message: result.message || `Connected successfully to ${selectedProvider} (${result.latencyMs}ms)`,
        });
      } else {
        showToast({
          type: 'error',
          title: 'Connection Failed',
          message: result.message || 'Unable to connect to provider.',
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Connection test failed';
      setTestResult({
        ok: false,
        latencyMs: 0,
        model: modelKey,
        message,
        error: 'REQUEST_FAILED',
      });
      setTestStatus('failed');
      showToast({
        type: 'error',
        title: 'Connection Failed',
        message,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminRequest('/models', {
        method: 'POST',
        body: JSON.stringify({
          providerKey: selectedProvider,
          modelKey,
          displayName,
          baseUrl,
          capabilities: Array.from(capabilities),
          pricingRates: {
            inputPerMillionTokens: Number(inputPrice) || 0,
            outputPerMillionTokens: Number(outputPrice) || 0,
            cacheReadPerMillionTokens: cachePrice ? Number(cachePrice) : null,
          },
          supportsStrictSchema: capabilities.has('structured_output'),
          maxInputTokens: Number(contextTokens) || 128000,
          maxOutputTokens: Number(maxOutputTokens) || 4096,
          status: 'active',
          apiKey: useExistingKey ? undefined : apiKey.trim() || undefined,
        }),
      });
      showToast({
        type: 'success',
        title: 'Model Added Successfully',
        message: `Model "${displayName}" (${modelKey}) was verified and added to catalog.`,
      });
      onSuccess(`Model ${displayName} added to catalog.`);
      onClose();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="form-grid provider-model-form" onSubmit={handleSubmit}>
      {/* 1. Provider Selector Dropdown */}
      <div className="span-2 provider-picker-section" ref={providerDropdownRef}>
        <div className="form-label-row">
          <span className="form-label-text">Provider</span>
        </div>

        <div className="provider-dropdown-container">
          <button
            type="button"
            className={`provider-dropdown-trigger ${isProviderDropdownOpen ? 'open' : ''}`}
            onClick={() => {
              setIsProviderDropdownOpen(!isProviderDropdownOpen);
              if (isDropdownOpen) setIsDropdownOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                setIsProviderDropdownOpen(false);
              }
            }}
            aria-haspopup="listbox"
            aria-expanded={isProviderDropdownOpen}
          >
            <div className="provider-trigger-content">
              <ProviderBadge provider={currentProviderConfig.key} size={15} badgeSize={24} />
              <span className="provider-trigger-title">{currentProviderConfig.name}</span>
              <span className="provider-trigger-badge">{currentProviderConfig.badge}</span>
              {configuredProviderInfo?.configured && (
                <span className="provider-trigger-status">
                  <Check size={11} /> Configured
                </span>
              )}
            </div>

            <span className="provider-chevron-wrap" aria-hidden="true">
              {isProviderDropdownOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </span>
          </button>

          {isProviderDropdownOpen && (
            <div className="provider-dropdown-menu" role="listbox">
              {PROVIDER_OPTIONS.map((prov) => {
                const isSelected = selectedProvider === prov.key;
                const isConfigured = providersData.find((p) => p.key === prov.key)?.configured;
                return (
                  <button
                    key={prov.key}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`provider-dropdown-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleProviderSelect(prov.key)}
                  >
                    <div className="provider-item-main">
                      <span className="provider-item-check-slot" aria-hidden="true">
                        {isSelected && <Check size={13} />}
                      </span>
                      <ProviderBadge provider={prov.key} size={15} badgeSize={24} />
                      <div className="provider-item-text">
                        <div className="provider-item-header">
                          <strong className="provider-item-name">{prov.name}</strong>
                          <span className="provider-item-badge">{prov.badge}</span>
                        </div>
                        {prov.description && (
                          <span className="provider-item-desc">{prov.description}</span>
                        )}
                      </div>
                    </div>

                    {isConfigured && (
                      <span className="provider-item-status">
                        <Check size={11} /> Configured
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 2. Provider API Key & Live API Connection */}
      <label className="span-2">
        <div className="form-label-row">
          <span className="form-label-text">
            <span>Provider API key</span>
            {selectedProvider === 'gemini' && (
              <span className="model-count-tag">Google AI Studio</span>
            )}
          </span>
          {configuredProviderInfo?.configured && (
            <label className="check-mini">
              <input
                type="checkbox"
                checked={useExistingKey}
                onChange={(e) => {
                  setUseExistingKey(e.target.checked);
                  if (e.target.checked) {
                    fetchLiveModelsForProvider(selectedProvider, undefined, true);
                  }
                }}
              />
              <span>Use active key ({configuredProviderInfo.maskedHint})</span>
            </label>
          )}
        </div>

        {!useExistingKey ? (
          <div className="input-action-group">
            <div className="input-with-eye">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={currentProviderConfig?.apiKeyPlaceholder || 'Enter provider API key...'}
                autoComplete="off"
              />
              <button
                type="button"
                className="inline-icon-btn"
                onClick={() => setShowApiKey(!showApiKey)}
                title={showApiKey ? 'Hide' : 'Show'}
              >
                {showApiKey ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <button
              type="button"
              className="live-fetch-action-btn"
              disabled={isFetchingLive || (!apiKey.trim() && selectedProvider !== 'openrouter')}
              onClick={handleFetchLiveModels}
              title="Fetch all available models and live pricing from provider API"
            >
              {isFetchingLive ? (
                <>
                  <Loader2 size={13} className="spin-icon" /> Fetching...
                </>
              ) : (
                <>
                  <Sparkles size={13} /> {selectedProvider === 'gemini' ? 'Fetch Google models' : 'Fetch live models'}
                </>
              )}
            </button>
            <button
              type="button"
              className="button button--subtle test-btn-inline"
              disabled={testStatus === 'testing' || !apiKey.trim()}
              onClick={handleTestConnection}
              title="Test connection with provider"
            >
              {testStatus === 'testing' ? (
                <>
                  <Loader2 size={13} className="spin-icon" /> Testing...
                </>
              ) : (
                <>
                  <Zap size={13} /> Test
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="configured-key-bar">
            <span className="configured-key-hint">
              <KeyRound size={13} /> Active platform credential: <code>{configuredProviderInfo?.maskedHint}</code>
            </span>
            <div className="label-actions-group">
              <button
                type="button"
                className="live-fetch-action-btn"
                disabled={isFetchingLive}
                onClick={handleFetchLiveModels}
              >
                {isFetchingLive ? (
                  <>
                    <Loader2 size={13} className="spin-icon" /> Fetching...
                  </>
                ) : (
                  <>
                    <Sparkles size={13} /> Fetch live models
                  </>
                )}
              </button>
              <button
                type="button"
                className="button button--subtle test-btn-inline"
                disabled={testStatus === 'testing'}
                onClick={handleTestConnection}
              >
                {testStatus === 'testing' ? (
                  <>
                    <Loader2 size={13} className="spin-icon" /> Testing...
                  </>
                ) : (
                  <>
                    <Zap size={13} /> Test connection
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {testResult && (
          <div className={`connection-feedback ${testResult.ok ? 'connection-feedback--ok' : 'connection-feedback--err'}`}>
            {testResult.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            <span>{testResult.message}</span>
          </div>
        )}
      </label>

      {/* 3. Preset or Live Model Selector */}
      <div className="span-2 model-picker-section" ref={dropdownRef}>
        <div className="form-label-row">
          <span className="form-label-text">
            <span>Model selection</span>
            {availableModels.length > 0 && (
              <span className="model-count-tag">
                {availableModels.length} models
              </span>
            )}
            {isFetchingLive ? (
              <span className="live-fetching-pill">
                <Loader2 size={10} className="spin-icon" /> Fetching live...
              </span>
            ) : isLiveCatalog ? (
              <span className="live-status-pill">
                <Sparkles size={10} /> Live {selectedProvider === 'gemini' ? 'Google' : selectedProvider === 'openrouter' ? 'OpenRouter' : 'API'} ({availableModels.length} models)
              </span>
            ) : (
              <span className="preset-status-pill">
                Preset Catalog ({availableModels.length})
              </span>
            )}
          </span>
          <div className="label-actions-group">
            <button
              type="button"
              className="inline-action-btn"
              onClick={handleFetchLiveModels}
              disabled={isFetchingLive}
              title="Refresh live models catalog from provider API"
            >
              {isFetchingLive ? <Loader2 size={12} className="spin-icon" /> : <RefreshCw size={12} />}
              <span>{isFetchingLive ? 'Fetching...' : 'Reload live models'}</span>
            </button>
            <button
              type="button"
              className="custom-toggle-btn"
              onClick={() => setIsCustomMode(!isCustomMode)}
            >
              {isCustomMode ? 'Choose from catalog' : 'Enter custom model'}
            </button>
          </div>
        </div>

        {!isLiveCatalog && selectedProvider === 'gemini' && !apiKey.trim() && !useExistingKey && (
          <div className="model-info-banner">
            <Sparkles size={14} />
            <span>
              Showing default Gemini presets. Enter your Gemini API key above and click <strong>Fetch Google models</strong> to dynamically load all models and live pricing from Google AI Studio (REST &amp; OpenAI endpoints).
            </span>
          </div>
        )}

        {isCustomMode ? (
          <input
            name="model"
            value={modelKey}
            onChange={(e) => setModelKey(e.target.value)}
            required
            placeholder="e.g. gpt-4o or gemini-2.5-flash"
          />
        ) : (
          <div className="model-dropdown-container">
            <button
              type="button"
              className={`model-dropdown-trigger ${isDropdownOpen ? 'open' : ''}`}
              onClick={() => {
                setIsDropdownOpen(!isDropdownOpen);
                if (isProviderDropdownOpen) setIsProviderDropdownOpen(false);
              }}
              aria-haspopup="listbox"
              aria-expanded={isDropdownOpen}
            >
              <div className="model-trigger-content">
                {selectedModelPreset ? (
                  <ProviderBadge provider={selectedProvider} modelKey={selectedModelPreset.modelKey} size={14} badgeSize={22} />
                ) : (
                  <ProviderBadge provider={selectedProvider} size={14} badgeSize={22} />
                )}
                {selectedModelPreset?.capabilities.includes('vision') && (
                  <span className="model-vision-badge" title="Multimodal / Vision model">
                    <Camera size={11} aria-hidden="true" />
                    <span>Vision</span>
                  </span>
                )}
                <span className="model-trigger-title">
                  {selectedModelPreset ? selectedModelPreset.displayName : (modelKey || 'Select a model...')}
                </span>
                {selectedModelPreset && (
                  <span className="model-trigger-key">
                    ({selectedModelPreset.modelKey})
                  </span>
                )}
              </div>

              <div className="model-trigger-end">
                {selectedModelPreset && selectedModelPreset.pricingRates.inputPerMillionTokens !== null && (
                  <span className="model-item-pricing-hint">
                    {formatPrice(selectedModelPreset.pricingRates.inputPerMillionTokens)} / {formatPrice(selectedModelPreset.pricingRates.outputPerMillionTokens)}
                  </span>
                )}
                {selectedModelPreset && (
                  <span className="model-ctx-chip">
                    {formatTokens(selectedModelPreset.maxInputTokens)}
                  </span>
                )}
                <span className="model-chevron-wrap" aria-hidden="true">
                  {isDropdownOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </span>
              </div>
            </button>

            {isDropdownOpen && (
              <div className="model-dropdown-menu" role="listbox">
                {/* Search Bar */}
                <div className="model-dropdown-search">
                  <Search size={13} className="model-search-icon" aria-hidden="true" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="model-search-input"
                    placeholder={`Search ${availableModels.length} models by name, key, or family (e.g. flash, pro, vision, 2.0)...`}
                    value={modelSearchQuery}
                    onChange={(e) => setModelSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setIsDropdownOpen(false);
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setActiveModelIndex((prev) => Math.min(prev + 1, Math.max(0, filteredModels.length - 1)));
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setActiveModelIndex((prev) => Math.max(prev - 1, 0));
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (filteredModels.length > 0 && filteredModels[activeModelIndex]) {
                          applyModelPreset(filteredModels[activeModelIndex]);
                          setIsDropdownOpen(false);
                          setModelSearchQuery('');
                        } else if (modelSearchQuery.trim()) {
                          setModelKey(modelSearchQuery.trim());
                          setDisplayName(modelSearchQuery.trim());
                          setIsDropdownOpen(false);
                          setModelSearchQuery('');
                        }
                      }
                    }}
                  />
                  {modelSearchQuery && (
                    <button
                      type="button"
                      className="model-search-clear"
                      onClick={() => setModelSearchQuery('')}
                      aria-label="Clear search"
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>

                <div className="model-dropdown-meta-bar">
                  <span>Showing {filteredModels.length} of {availableModels.length} models</span>
                  {isLiveCatalog ? (
                    <span className="model-live-indicator live-active">
                      <Sparkles size={10} /> Live API
                    </span>
                  ) : (
                    <span className="model-live-indicator live-preset">
                      Presets
                    </span>
                  )}
                </div>

                <div className="model-dropdown-list" ref={modelListRef}>
                  {filteredModels.length === 0 ? (
                    <div className="model-dropdown-empty">
                      <span>No models matched &ldquo;{modelSearchQuery}&rdquo;</span>
                      {modelSearchQuery && (
                        <button
                          type="button"
                          className="model-use-custom-btn"
                          onClick={() => {
                            setModelKey(modelSearchQuery.trim());
                            setDisplayName(modelSearchQuery.trim());
                            setIsDropdownOpen(false);
                          }}
                        >
                          + Use &ldquo;{modelSearchQuery.trim()}&rdquo; as custom model
                        </button>
                      )}
                    </div>
                  ) : (
                    filteredModels.map((m, idx) => {
                      const isSelected = m.modelKey === modelKey;
                      const isHighlighted = idx === activeModelIndex;
                      const isVision = m.capabilities.includes('vision');
                      return (
                        <button
                          key={m.modelKey}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          className={`model-dropdown-item ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                          onMouseEnter={() => setActiveModelIndex(idx)}
                          onClick={() => {
                            applyModelPreset(m);
                            setIsDropdownOpen(false);
                            setModelSearchQuery('');
                          }}
                        >
                          <div className="model-item-main">
                            <span className="model-item-check-slot" aria-hidden="true">
                              {isSelected && <Check size={13} />}
                            </span>
                            <ProviderBadge provider={selectedProvider} modelKey={m.modelKey} size={14} badgeSize={22} />
                            <div className="model-item-text">
                              <strong className="model-item-name">{m.displayName}</strong>
                              <div className="model-item-meta-line">
                                <span className="model-item-key">{m.modelKey}</span>
                                {m.pricingRates.inputPerMillionTokens !== null && (
                                  <span className="model-item-pricing-hint">
                                    {formatPrice(m.pricingRates.inputPerMillionTokens)} / {formatPrice(m.pricingRates.outputPerMillionTokens)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="model-item-badges">
                            {isVision && (
                              <span className="model-vision-badge" title="Multimodal / Vision model">
                                <Camera size={11} aria-hidden="true" />
                                <span>Vision</span>
                              </span>
                            )}
                            <span className="model-ctx-badge">
                              {formatTokens(m.maxInputTokens)}
                            </span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {liveFetchError && (
          <span className="form-error">
            <AlertCircle size={13} /> {liveFetchError}
          </span>
        )}
      </div>

      {/* 4. Provider & Model Technical Identifiers */}
      <label>
        Provider key
        <input
          name="provider"
          value={selectedProvider}
          onChange={(e) => setSelectedProvider(e.target.value.toLowerCase())}
          required
          readOnly={selectedProvider !== 'custom'}
        />
      </label>

      <label>
        Model key
        <input
          name="model"
          value={modelKey}
          onChange={(e) => setModelKey(e.target.value)}
          required
          placeholder="e.g. claude-3-7-sonnet"
        />
      </label>

      <label className="span-2">
        Display name
        <input
          name="display"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
          placeholder="e.g. Claude 3.7 Sonnet"
        />
      </label>

      <label className="span-2">
        Provider base URL
        <input
          name="baseUrl"
          type="url"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          required
        />
      </label>

      {/* 5. Capabilities */}
      <fieldset className="span-2">
        <legend>Capabilities</legend>
        <label className="check">
          <input
            type="checkbox"
            checked={capabilities.has('text')}
            onChange={() => toggleCapability('text')}
          />
          Text
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={capabilities.has('vision')}
            onChange={() => toggleCapability('vision')}
          />
          Vision
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={capabilities.has('structured_output')}
            onChange={() => toggleCapability('structured_output')}
          />
          Structured output
        </label>
      </fieldset>

      {/* 6. Pricing & Token Limits */}
      <label>
        Input USD / 1M
        <input
          name="inputPrice"
          type="number"
          min="0"
          step="0.0001"
          value={inputPrice}
          onChange={(e) => setInputPrice(e.target.value)}
          required
        />
      </label>

      <label>
        Output USD / 1M
        <input
          name="outputPrice"
          type="number"
          min="0"
          step="0.0001"
          value={outputPrice}
          onChange={(e) => setOutputPrice(e.target.value)}
          required
        />
      </label>

      <label>
        Cache read USD / 1M
        <input
          name="cachePrice"
          type="number"
          min="0"
          step="0.0001"
          value={cachePrice}
          onChange={(e) => setCachePrice(e.target.value)}
          placeholder="Optional"
        />
      </label>

      <label>
        Context tokens
        <input
          name="context"
          type="number"
          min="1024"
          value={contextTokens}
          onChange={(e) => setContextTokens(e.target.value)}
          required
        />
      </label>

      <label className="span-2">
        Max output tokens
        <input
          name="output"
          type="number"
          min="1"
          value={maxOutputTokens}
          onChange={(e) => setMaxOutputTokens(e.target.value)}
          required
        />
      </label>

      <button className="button button--primary span-2" disabled={saving || !modelKey.trim()}>
        {saving ? 'Saving...' : 'Verify and add model'}
      </button>
    </form>
  );
}
